import { CommonErrorCode } from '~commonInfra/utils/ErrorCodes';
import { Tableau } from '~commonInfra/utils/TableauLog';

import { IImportManifest, IImportManifestItem } from '../serverApi/types/IBundleImport';
import { IExecution } from '../serverApi/types/IExecution';
import { cancelImportExecution, getImportManifest, IImportPreviewData, loadImportPreview, triggerBundleImport } from './bundleImportActions';
import { getUploadExecutionStatus } from './bundleUploadActions';
import { MAX_CONSECUTIVE_POLL_FAILURES, MAX_POLL_BACKOFF_MS, MAX_TRACKING_MS, POLL_INTERVAL_MS } from './BundleUploadController';

/**
 * The import dialog's phases:
 *
 *   loading (read the preview) -> select (pick items, confirm overwrites) -> starting (queue the import)
 *          -> tracking (poll the execution) -> succeeded | failed | cancelled
 *
 * plus loadError (couldn't read the preview), cancelling (waiting for the server to confirm a cancel),
 * statusUnavailable (lost track of a queued import — it may still finish), and two dead ends the server
 * reports for the bundle: alreadyImported (imported already, or an import is in progress) and
 * unavailable (this site can't import content relay packages yet).
 */
export type BundleImportPhase =
  | 'loading'
  | 'loadError'
  | 'select'
  | 'starting'
  | 'tracking'
  | 'cancelling'
  | 'succeeded'
  | 'failed'
  | 'cancelled'
  | 'statusUnavailable'
  | 'alreadyImported'
  | 'unavailable';

/**
 * Why the last Import click didn't queue the import: `confirmRequired` when the server needs the overwrite
 * confirmation the preview didn't ask for (CONFIRMATION_REQUIRED, e.g. the site changed since), `failed`
 * for anything else.
 */
export type ImportStartError = 'failed' | 'confirmRequired';

export type ImportManifestStatus = 'none' | 'loading' | 'loaded' | 'error';

/** The manifest pages read so far: the summary of the first page, and every item loaded. */
export interface IImportManifestData {
  readonly createdCount: number;
  readonly overwrittenCount: number;
  readonly failedCount: number;
  readonly items: ReadonlyArray<IImportManifestItem>;
  readonly nextPageToken?: string;
}

export interface BundleImportState {
  phase: BundleImportPhase;
  preview?: IImportPreviewData;
  /** The last Import click couldn't queue the import (shown on the select step). */
  startError?: ImportStartError;
  /** The latest polled execution once the import is queued. */
  execution?: IExecution;
  manifest?: IImportManifestData;
  manifestStatus: ImportManifestStatus;
}

/** The server calls the controller drives; injectable so the import logic can be unit tested. */
export interface BundleImportApi {
  preview: (bundleLuid: string) => Promise<IImportPreviewData>;
  trigger: (bundleLuid: string, selectedItemIds: string[] | undefined, confirmOverwrite: boolean) => Promise<number>;
  getStatus: (backgroundJobId: number) => Promise<IExecution>;
  cancel: (backgroundJobId: number) => Promise<IExecution>;
  getManifest: (backgroundJobId: number, pageToken?: string) => Promise<IImportManifest>;
}

export const INITIAL_BUNDLE_IMPORT_STATE: BundleImportState = { phase: 'loading', manifestStatus: 'none' };

const DEFAULT_API: BundleImportApi = {
  preview: loadImportPreview,
  trigger: triggerBundleImport,
  // Status is generic across extract / upload / import executions.
  getStatus: getUploadExecutionStatus,
  cancel: cancelImportExecution,
  getManifest: getImportManifest,
};

const TERMINAL_PHASES: { [state: string]: BundleImportPhase } = {
  SUCCEEDED: 'succeeded',
  FAILED: 'failed',
  CANCELLED: 'cancelled',
};

function terminalPhase(execution: IExecution): BundleImportPhase | undefined {
  return TERMINAL_PHASES[(execution.state || '').toUpperCase()];
}

function logInfo(message: string): void {
  Tableau.Logger().info('Content Relay import: ' + message);
}

/** The dead end a server error code means for this bundle, if any. */
function deadEndPhase(error: any): BundleImportPhase | undefined {
  if (error === CommonErrorCode.InProgress) {
    return 'alreadyImported';
  }
  return error === CommonErrorCode.FeatureNotEnabled ? 'unavailable' : undefined;
}

/**
 * The import never reached the service (canceled while queued, or failed before the hand-off): the
 * monolith reports the job's own state without an execution, so there is no manifest and nothing was
 * imported.
 */
export function neverStarted(execution: IExecution | undefined): boolean {
  return !!execution && !execution.luid;
}

/**
 * Drives the import of one uploaded bundle: preview -> trigger -> poll -> manifest. Polling uses the
 * upload dialog's interval, backoff and limits. Closing the dialog once the import is queued stops the
 * client poll only, and the import keeps running server-side; closing it while the import is still being
 * queued cancels it as soon as it is.
 */
export class BundleImportController {
  private state: BundleImportState = INITIAL_BUNDLE_IMPORT_STATE;
  private disposed = false;
  private backgroundJobId: number | undefined;
  private pollTimer: number | undefined;
  private pollFailures = 0;
  private trackingStartedAtMs = 0;

  constructor(
    private readonly bundleLuid: string,
    private readonly onChange: (state: BundleImportState) => void,
    private readonly onCancelError: () => void,
    private readonly api: BundleImportApi = DEFAULT_API
  ) {}

  public getState(): BundleImportState {
    return this.state;
  }

  /** Reads the bundle's preview (also Try again after a failed read). */
  public load(): void {
    if (this.disposed || (this.state.phase !== 'loading' && this.state.phase !== 'loadError')) {
      return;
    }
    this.readPreview(undefined);
  }

  /**
   * Queues the import. `selectedItemIds` limits it to those items (undefined imports the whole bundle);
   * `confirmOverwrite` records that the user confirmed overwriting existing content.
   */
  public start(selectedItemIds: string[] | undefined, confirmOverwrite: boolean): void {
    if (this.disposed || this.state.phase !== 'select') {
      return;
    }
    this.setState({ phase: 'starting', startError: undefined });
    const self = this;
    this.api.trigger(this.bundleLuid, selectedItemIds, confirmOverwrite).then(function(backgroundJobId) {
      self.backgroundJobId = backgroundJobId;
      self.trackingStartedAtMs = Date.now();
      if (self.disposed) {
        // The dialog was closed (Cancel / X) before the import was queued: don't let it run.
        self.api.cancel(backgroundJobId).catch(function(error) {
          logInfo('couldn\'t cancel job ' + backgroundJobId + ' queued after the dialog closed: ' + error);
        });
        return;
      }
      self.setState({ phase: 'tracking' });
      self.poll();
    }, function(error) {
      logInfo('couldn\'t start the import: ' + error);
      if (self.disposed) {
        return;
      }
      const deadEnd = deadEndPhase(error);
      if (deadEnd) {
        self.setState({ phase: deadEnd });
        return;
      }
      if (error === CommonErrorCode.ConfirmationRequired) {
        // Something changed since the preview: show the current one and ask for the confirmation.
        self.readPreview('confirmRequired');
        return;
      }
      self.setState({ phase: 'select', startError: 'failed' });
    });
  }

  /**
   * Cancels a queued or running import. Stays cancelling, polling the execution, until the server reports
   * it has actually stopped.
   */
  public cancel(): void {
    if (this.disposed || this.state.phase !== 'tracking') {
      return;
    }
    this.stopPolling();
    this.setState({ phase: 'cancelling' });
    const self = this;
    this.api.cancel(this.backgroundJobId).then(function(execution) {
      if (!self.disposed) {
        self.applyStatus(execution);
      }
    }, function(error) {
      logInfo('couldn\'t cancel job ' + self.backgroundJobId + ': ' + error);
      if (self.disposed) {
        return;
      }
      // The cancel didn't go through (e.g. the import finished first): show what actually happened.
      self.onCancelError();
      self.setState({ phase: 'tracking' });
      self.api.getStatus(self.backgroundJobId).then(function(execution) {
        if (!self.disposed) {
          self.applyStatus(execution);
        }
      }, function() {
        if (!self.disposed) {
          self.setState({ phase: 'statusUnavailable' });
        }
      });
    });
  }

  /** Reads the next manifest page, or retries a manifest read that failed. */
  public loadMoreManifest(): void {
    const manifest = this.state.manifest;
    if (this.disposed || this.state.manifestStatus === 'loading' || (manifest && !manifest.nextPageToken)
      || neverStarted(this.state.execution)) {
      return;
    }
    this.loadManifest(manifest ? manifest.nextPageToken : undefined);
  }

  /** The dialog closed: stop polling. A queued import keeps running server-side. */
  public dispose(): void {
    this.disposed = true;
    this.stopPolling();
  }

  /** Reads the preview, then shows it for selection with `startError` (if any) on it. */
  private readPreview(startError: ImportStartError | undefined): void {
    this.setState({ phase: 'loading' });
    const self = this;
    this.api.preview(this.bundleLuid).then(function(preview) {
      if (!self.disposed) {
        self.setState({ phase: 'select', preview: preview, startError: startError });
      }
    }, function(error) {
      logInfo('couldn\'t read the preview: ' + error);
      if (!self.disposed) {
        self.setState({ phase: deadEndPhase(error) || 'loadError', startError: undefined });
      }
    });
  }

  private poll(): void {
    const self = this;
    this.api.getStatus(this.backgroundJobId).then(function(execution) {
      if (!self.isPolling()) {
        return;
      }
      self.applyStatus(execution);
    }, function() {
      if (!self.isPolling()) {
        return;
      }
      self.pollFailures++;
      if (self.pollFailures >= MAX_CONSECUTIVE_POLL_FAILURES || self.trackingTimedOut()) {
        self.setState({ phase: 'statusUnavailable' });
        return;
      }
      // A network blip or 5xx doesn't mean the import failed: back off and try again.
      self.schedulePoll(Math.min(MAX_POLL_BACKOFF_MS, POLL_INTERVAL_MS * Math.pow(2, self.pollFailures)));
    });
  }

  /** Applies a status the server reported: an end state, or keep tracking (or cancelling) and poll again. */
  private applyStatus(execution: IExecution): void {
    this.pollFailures = 0;
    const phase = terminalPhase(execution);
    if (phase) {
      this.finish(phase, execution);
      return;
    }
    this.setState({ phase: this.state.phase === 'cancelling' ? 'cancelling' : 'tracking', execution: execution });
    if (this.trackingTimedOut()) {
      this.setState({ phase: 'statusUnavailable' });
      return;
    }
    this.schedulePoll(POLL_INTERVAL_MS);
  }

  /**
   * An end state: record it and read the manifest (a failed or canceled import may have written some
   * items), unless the import never reached the service.
   */
  private finish(phase: BundleImportPhase, execution: IExecution): void {
    if (phase === 'failed') {
      // Not shown to the user; logged for support.
      logInfo('job ' + this.backgroundJobId + ' failed: ' + (execution.errorCode || '') + ' ' + (execution.errorMessage || ''));
    }
    this.setState({ phase: phase, execution: execution, manifest: undefined, manifestStatus: 'none' });
    if (!neverStarted(execution)) {
      this.loadManifest(undefined);
    }
  }

  private loadManifest(pageToken: string | undefined): void {
    this.setState({ manifestStatus: 'loading' });
    const self = this;
    this.api.getManifest(this.backgroundJobId, pageToken).then(function(page) {
      if (self.disposed) {
        return;
      }
      const previous = self.state.manifest;
      const items = (previous ? previous.items : []).concat(page.items || []);
      self.setState({
        manifestStatus: 'loaded',
        manifest: {
          createdCount: previous ? previous.createdCount : page.createdCount || 0,
          overwrittenCount: previous ? previous.overwrittenCount : page.overwrittenCount || 0,
          failedCount: previous ? previous.failedCount : page.failedCount || 0,
          items: items,
          nextPageToken: page.nextPageToken || undefined,
        },
      });
    }, function(error) {
      logInfo('couldn\'t read the manifest of job ' + self.backgroundJobId + ': ' + error);
      if (self.disposed) {
        return;
      }
      // No execution to read the manifest of: show the result without details rather than a retry.
      const noDetails = error === CommonErrorCode.ContentNotFound && !self.state.manifest;
      self.setState({ manifestStatus: noDetails ? 'none' : 'error' });
    });
  }

  private schedulePoll(delayMs: number): void {
    const self = this;
    this.stopPolling();
    this.pollTimer = window.setTimeout(function() {
      self.pollTimer = undefined;
      self.poll();
    }, delayMs);
  }

  private stopPolling(): void {
    if (this.pollTimer !== undefined) {
      window.clearTimeout(this.pollTimer);
      this.pollTimer = undefined;
    }
  }

  /** The execution is being polled: while tracking, and while waiting for a cancel to take effect. */
  private isPolling(): boolean {
    return !this.disposed && (this.state.phase === 'tracking' || this.state.phase === 'cancelling');
  }

  private trackingTimedOut(): boolean {
    return Date.now() - this.trackingStartedAtMs > MAX_TRACKING_MS;
  }

  private setState(partial: Partial<BundleImportState>): void {
    this.state = { ...this.state, ...partial };
    if (!this.disposed) {
      this.onChange(this.state);
    }
  }
}
